import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { Box } from "@mui/material";

import { useLargeReportConfirm } from "src/hooks/useLargeReportConfirm";
import { useReportRangeLimit } from "src/hooks/useReportRangeLimit";

import { ActionButton, CardLayout, SelectField } from "../v3";
import { AdminService, CRMService, ProjectService } from "../../services";
import { getFilteredOptions, selectAllOption, selectAllUsers } from "../../utils";
import { clientReportValidation } from "../../pages/report/validation";
import { ReportDateRangeFields } from "./ReportDateRangeFields.component";

export const ClientReportForm = ({ reportType = "summary", handleClear = () => {}, onGenerate = () => {} }) => {
  const { months: maxRangeMonths } = useReportRangeLimit();
  const { confirmLargeRange, largeRangeDialog } = useLargeReportConfirm();
  const [selectedClients, setSelectedClients] = useState([selectAllOption]);
  const [selectedProjects, setSelectedProjects] = useState([selectAllOption]);
  const [selectedUsers, setSelectedUsers] = useState([selectAllUsers]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [reportDetails, setReportDetails] = useState({
    clientIds: null,
    projectIds: null,
    createdByIds: null,
    fromDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("clientId");

  useEffect(() => {
    handleClear();
    setLoading(false);
  }, [reportType]);

  useEffect(() => {
    if (!clientId) return;

    const fetchClient = async () => {
      setLoading(true);
      try {
        const { data: client } = await CRMService.getClientById(clientId);
        setSelectedClients([client]);
        setReportDetails((prev) => ({
          ...prev,
          clientIds: [client.id],
        }));
        setErrors((prev) => ({ ...prev, clientIds: "" }));
      } catch (error) {
        toast.error("Failed to fetch client details");
        console.error("🚀 ~ fetchClient ~ error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchClient();
  }, [clientId]);

  const updateDetailsState = (event) => {
    setLoading(false);
    handleClear();
    const { name, value } = event.target;
    setReportDetails((prev) => ({ ...prev, [name]: value }));
    if (name === "fromDate") setErrors((prev) => ({ ...prev, fromDate: "" }));
    else if (name === "toDate") setErrors((prev) => ({ ...prev, toDate: "" }));
  };

  const handleClientChange = (clients) => {
    setLoading(false);
    handleClear();
    const allSelected = clients.some((client) => client.id === "all");

    if (allSelected) {
      setSelectedClients([selectAllOption]);
      setReportDetails((prev) => ({ ...prev, clientIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = clients.filter((client) => client.id !== "all");
      setSelectedClients(filtered);
      setReportDetails((prev) => ({ ...prev, clientIds: filtered.map((client) => client.id) }));
    }

    setErrors((prev) => ({ ...prev, clientIds: "" }));
  };

  const handleProjectChange = (projects) => {
    setLoading(false);
    handleClear();
    const allSelected = projects.some((project) => project.id === "all");

    if (allSelected) {
      setSelectedProjects([selectAllOption]);
      setReportDetails((prev) => ({ ...prev, projectIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = projects.filter((project) => project.id !== "all");
      setSelectedProjects(filtered);
      setReportDetails((prev) => ({ ...prev, projectIds: filtered.map((project) => project.id) }));
    }

    setErrors((prev) => ({ ...prev, projectIds: "" }));
  };

  const handleUserChange = (users) => {
    setLoading(false);
    handleClear();
    const allSelected = users.some((user) => user.id === "all");

    if (allSelected) {
      setSelectedUsers([selectAllUsers]);
      setReportDetails((prev) => ({ ...prev, createdByIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = users.filter((user) => user.id !== "all");
      setSelectedUsers(filtered);
      setReportDetails((prev) => ({ ...prev, createdByIds: filtered.map((user) => user.id) }));
    }

    setErrors((prev) => ({ ...prev, createdByIds: "" }));
  };

  const handleGenerateReport = async () => {
    setErrors({});
    const errors = clientReportValidation(
      reportDetails,
      selectedClients[0]?.id,
      selectedProjects[0]?.id,
      selectedUsers[0]?.id,
      maxRangeMonths,
    );
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    if (!(await confirmLargeRange(reportDetails.fromDate, reportDetails.toDate))) return;

    try {
      setLoading(true);

      const isoFromDate = moment(reportDetails.fromDate).startOf("day").toISOString();
      const isoToDate = moment(reportDetails.toDate).endOf("day").toISOString();
      const payload = {
        ...reportDetails,
        fromDate: isoFromDate,
        toDate: isoToDate,
        reportType,
      };

      onGenerate?.(payload, {
        onError: () => setLoading(false),
      });
    } catch (error) {
      setLoading(false);
      toast.error("Failed to generate report");
      console.error("🚀 ~ handleGenerateReport ~ error:", error);
    }
  };

  return (
    <CardLayout>
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <SelectField
            label="Client"
            value={selectedClients}
            getData={CRMService.getClientList}
            getDataStaticFilters={{ type: "INDIVIDUAL" }}
            disabled={!!clientId && selectedClients.length > 0 && selectedClients[0]?.id === clientId}
            required
            multiSelect
            error={!!errors.clientIds}
            helperText={errors.clientIds}
            onChange={handleClientChange}
            optionFilter={(options) => getFilteredOptions(options, selectedClients, selectAllOption)}
            style={{ width: "100%" }}
          />
        </Box>
        <Box display="flex" gap={2}>
          <SelectField
            label="Project"
            value={selectedProjects}
            getData={ProjectService.getProjectList}
            getDataStaticFilters={{ status: ["INPROGRESS"] }}
            required
            multiSelect
            error={!!errors.projectIds}
            helperText={errors.projectIds}
            onChange={handleProjectChange}
            optionFilter={(options) => getFilteredOptions(options, selectedProjects, selectAllOption)}
            style={{ width: "100%" }}
          />
          <SelectField
            label="Entry By"
            value={selectedUsers}
            getData={AdminService.getUserList}
            getDataStaticFilters={{ type: "USER" }}
            optionValueFields={["firstName", "lastName"]}
            required
            multiSelect
            error={!!errors.createdByIds}
            helperText={errors.createdByIds}
            onChange={handleUserChange}
            optionFilter={(options) => getFilteredOptions(options, selectedUsers, selectAllUsers)}
            style={{ width: "100%" }}
          />
        </Box>
        <ReportDateRangeFields
          fromDate={reportDetails.fromDate}
          toDate={reportDetails.toDate}
          onChange={updateDetailsState}
          errors={errors}
        />
        {largeRangeDialog}
      </Box>
      <Box display="flex" justifyContent="flex-end">
        <ActionButton buttonText="Generate" color="primary" disabled={loading} onButtonClick={handleGenerateReport} />
      </Box>
    </CardLayout>
  );
};
